import { useRef, useCallback, useEffect } from 'react';
import {
  LAYOUT_ANIMATION_DURATION,
  EDGE_FADE_IN_DURATION,
  LAYOUT_BLOCKER_SAFETY_MS,
} from './constants';

/**
 * Layout tidy animation: hide edges while nodes slide, then fade edges back in.
 *
 * - `startLayoutEdgeHiddenPhase` — call as soon as Tidy is clicked (before ELK),
 *   so edges disappear while new routes are computed and nodes move.
 * - `scheduleLayoutEdgeRevealAfter` — after node motion duration, show edges again
 *   (call from ELK completion when hide was already started).
 * - `triggerEdgeFadeIn` — start hide + schedule reveal in one call (multi-campaign
 *   and other paths that do not pre-call start).
 *
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setIsAnimatingLayout
 * @param {{ onLayoutRevealPhaseComplete?: () => void }} [options] — e.g. clear a Tidy toolbar session when edges are revealed.
 */
export function useLayoutEdgeFadeChoreography(
  setIsAnimatingLayout,
  { onLayoutRevealPhaseComplete } = {},
) {
  const layoutAnimationTimeoutRef = useRef(null);
  const edgeFadeTimeoutRef = useRef(null);
  const revealRafRef = useRef(null);
  const safetyTimeoutRef = useRef(null);

  const clearSafetyTimer = useCallback(() => {
    if (safetyTimeoutRef.current != null) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  const clearLayoutTimers = useCallback(() => {
    if (layoutAnimationTimeoutRef.current) {
      clearTimeout(layoutAnimationTimeoutRef.current);
      layoutAnimationTimeoutRef.current = null;
    }
    if (edgeFadeTimeoutRef.current) {
      clearTimeout(edgeFadeTimeoutRef.current);
      edgeFadeTimeoutRef.current = null;
    }
    if (revealRafRef.current != null) {
      cancelAnimationFrame(revealRafRef.current);
      revealRafRef.current = null;
    }
  }, []);

  const runLayoutRevealPhase = useCallback(() => {
    // Defer the reveal by two animation frames so the edge path cache has committed the freshly
    // laid-out ELK paths before edges fade back in. Revealing synchronously (in the same tick ELK
    // state is committed) can paint one frame of the previous getEdgePath routing before the cache
    // adopts the ELK paths — the visible "wrong paths flash then snap to correct" right after Tidy.
    clearSafetyTimer();
    if (revealRafRef.current != null) cancelAnimationFrame(revealRafRef.current);
    revealRafRef.current = requestAnimationFrame(() => {
      revealRafRef.current = requestAnimationFrame(() => {
        revealRafRef.current = null;
        setIsAnimatingLayout(false);
        onLayoutRevealPhaseComplete?.();
        const blockerEnd = document.querySelector('.flowchart__animation-blocker');
        if (blockerEnd) blockerEnd.style.display = '';

        const edges = document.querySelectorAll('.flowchart__edge');
        edges.forEach((edge) => edge.classList.add('flowchart__edge--reorder-fade-in'));

        edgeFadeTimeoutRef.current = setTimeout(() => {
          edgeFadeTimeoutRef.current = null;
          edges.forEach((edge) => edge.classList.remove('flowchart__edge--reorder-fade-in'));
        }, EDGE_FADE_IN_DURATION + 50);
      });
    });
  }, [setIsAnimatingLayout, onLayoutRevealPhaseComplete, clearSafetyTimer]);

  const startLayoutEdgeHiddenPhase = useCallback(() => {
    clearLayoutTimers();
    clearSafetyTimer();
    setIsAnimatingLayout(true);
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker) blocker.style.display = 'block';
    // If ELK never reveals (aborted / skipped / hung), unlock the canvas.
    safetyTimeoutRef.current = setTimeout(() => {
      safetyTimeoutRef.current = null;
      runLayoutRevealPhase();
    }, LAYOUT_BLOCKER_SAFETY_MS);
  }, [clearLayoutTimers, clearSafetyTimer, setIsAnimatingLayout, runLayoutRevealPhase]);

  const scheduleLayoutEdgeRevealAfter = useCallback(
    (layoutDurationMs) => {
      clearLayoutTimers();
      if (layoutDurationMs <= 0) {
        runLayoutRevealPhase();
        return;
      }
      layoutAnimationTimeoutRef.current = setTimeout(() => {
        layoutAnimationTimeoutRef.current = null;
        runLayoutRevealPhase();
      }, layoutDurationMs + 50);
    },
    [clearLayoutTimers, runLayoutRevealPhase],
  );

  const triggerEdgeFadeIn = useCallback(
    (layoutDurationMs = LAYOUT_ANIMATION_DURATION) => {
      if (layoutDurationMs <= 0) {
        return;
      }
      startLayoutEdgeHiddenPhase();
      scheduleLayoutEdgeRevealAfter(layoutDurationMs);
    },
    [startLayoutEdgeHiddenPhase, scheduleLayoutEdgeRevealAfter],
  );

  useEffect(() => () => {
    clearLayoutTimers();
    clearSafetyTimer();
  }, [clearLayoutTimers, clearSafetyTimer]);

  return {
    layoutAnimationTimeoutRef,
    edgeFadeTimeoutRef,
    startLayoutEdgeHiddenPhase,
    scheduleLayoutEdgeRevealAfter,
    triggerEdgeFadeIn,
  };
}
